/**
 * Pool-type identity paint. These shades are identity, not status.
 * A green Strip Card chip never means "alive" and a red Brackets chip
 * never means "eliminated". Status on this platform is always color plus
 * text, and the pool card already renders statusLabel as text.
 */

import type { typeColorMode, typePalette } from "./tokens";
import { palette } from "./tokens";

export const CONTEST_TYPE_KEYS = [
  "survivor",
  "squares",
  "strip-card",
  "pickem",
  "brackets",
  "fantasy",
] as const;

export type typeContestType = (typeof CONTEST_TYPE_KEYS)[number];

/** One paintable pool identity: the chip fill, and the color drawn on it. */
export type typeContestShade = {
  readonly fill: string;
  readonly on: string;
};

export const contestHueFamily: Record<typeContestType, keyof typePalette> = {
  survivor: "blue",
  squares: "stadium",
  "strip-card": "green",
  pickem: "pink",
  brackets: "red",
  fantasy: "purple",
};

export const contestShades: Record<
  typeColorMode,
  Record<typeContestType, readonly typeContestShade[]>
> = {
  light: {
    survivor: [
      { fill: palette.blue[400], on: palette.neutral[950] },
      { fill: palette.blue[600], on: palette.neutral[0] },
      { fill: palette.blue[700], on: palette.neutral[0] },
      { fill: palette.blue[900], on: palette.neutral[0] },
    ],
    squares: [
      { fill: palette.stadium[600], on: palette.neutral[0] },
      { fill: palette.stadium[700], on: palette.neutral[0] },
      { fill: palette.stadium[800], on: palette.neutral[0] },
    ],
    "strip-card": [
      { fill: palette.green[600], on: palette.neutral[0] },
      { fill: palette.green[700], on: palette.neutral[0] },
      { fill: palette.green[800], on: palette.neutral[0] },
      { fill: palette.green[900], on: palette.neutral[0] },
    ],
    pickem: [
      { fill: palette.pink[500], on: palette.neutral[0] },
      { fill: palette.pink[600], on: palette.neutral[0] },
      { fill: palette.pink[700], on: palette.neutral[0] },
      { fill: palette.pink[900], on: palette.neutral[0] },
    ],
    brackets: [
      { fill: palette.red[500], on: palette.neutral[0] },
      { fill: palette.red[700], on: palette.neutral[0] },
      { fill: palette.red[800], on: palette.neutral[0] },
      { fill: palette.red[900], on: palette.neutral[0] },
    ],
    fantasy: [
      { fill: palette.purple[400], on: palette.neutral[0] },
      { fill: palette.purple[500], on: palette.neutral[0] },
      { fill: palette.purple[600], on: palette.neutral[0] },
      { fill: palette.purple[800], on: palette.neutral[0] },
    ],
  },
  dark: {
    survivor: [
      { fill: palette.blue[100], on: palette.neutral[975] },
      { fill: palette.blue[300], on: palette.neutral[975] },
      { fill: palette.blue[400], on: palette.neutral[975] },
      { fill: palette.blue[500], on: palette.neutral[0] },
    ],
    squares: [
      { fill: palette.stadium[100], on: palette.neutral[975] },
      { fill: palette.stadium[300], on: palette.neutral[975] },
      { fill: palette.stadium[400], on: palette.neutral[975] },
    ],
    "strip-card": [
      { fill: palette.green[100], on: palette.neutral[975] },
      { fill: palette.green[300], on: palette.neutral[975] },
      { fill: palette.green[400], on: palette.neutral[975] },
      { fill: palette.green[600], on: palette.neutral[0] },
    ],
    pickem: [
      { fill: palette.pink[100], on: palette.neutral[975] },
      { fill: palette.pink[300], on: palette.neutral[975] },
      { fill: palette.pink[400], on: palette.neutral[975] },
      { fill: palette.pink[500], on: palette.neutral[0] },
    ],
    brackets: [
      { fill: palette.red[100], on: palette.neutral[975] },
      { fill: palette.red[200], on: palette.neutral[975] },
      { fill: palette.red[300], on: palette.neutral[975] },
      { fill: palette.red[500], on: palette.neutral[0] },
    ],
    fantasy: [
      { fill: palette.purple[100], on: palette.neutral[975] },
      { fill: palette.purple[200], on: palette.neutral[975] },
      { fill: palette.purple[300], on: palette.neutral[975] },
      { fill: palette.purple[400], on: palette.neutral[0] },
    ],
  },
};

export const isContestType = (value: string): value is typeContestType =>
  (CONTEST_TYPE_KEYS as readonly string[]).includes(value);

/** FNV-1a 32-bit over poolId, unsigned, modulo shadeCount. */
export const contestShadeIndex = (
  poolId: string,
  shadeCount: number,
): number => {
  if (shadeCount <= 0) {
    return 0;
  }
  let hash = 2166136261;
  for (let i = 0; i < poolId.length; i++) {
    hash ^= poolId.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % shadeCount;
};

/** The pool's own shade, or null when contestType is not one of the six. */
export const contestShadeForPool = (input: {
  contestType: string;
  poolId: string;
  mode: typeColorMode;
}): typeContestShade | null => {
  if (!isContestType(input.contestType)) {
    return null;
  }
  const list = contestShades[input.mode][input.contestType];
  const shade = list[contestShadeIndex(input.poolId, list.length)];
  return shade ?? null;
};
